import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';

import { LayoutSplashScreen } from '../../../_metronic';
import getMenuConfig from '../../router/MenuConfig';
import * as builder from '../../../_metronic/ducks/builder';
import { actions as cartActions } from '../../store/ducks/cart.duck';
import { actions as supplyActions } from '../../store/ducks/supply.duck';
import { actions as profileActions } from '../../store/ducks/profile.duck';
import { IAppState } from '../../store/rootDuck';
import { UserRoles } from '../../interfaces/user';
import Preloader from '../../components/other/Preloader/Preloader';
import CompanyView from './companies/CompanyView';
import ProductView from './products/ProductView';
import BlogPage from './blog';
import { PrivateRoute } from '../../router/Routes';

const CategoriesPage = lazy(() => import('./categories'));
const UsersPage = lazy(() => import('./users'));
const CompaniesPage = lazy(() => import('./companies'));
const PromocodesPage = lazy(() => import('./promocodes'));
const RefferalsPage = lazy(() => import('./referrals'));
const UserDocPage = lazy(() => import('./userDocs'));
const SettingsPage = lazy(() => import('./settings'));
const ProductsPage = lazy(() => import('./products'));
const OrdersPage = lazy(() => import('./orders'));
const DeliveryPage = lazy(() => import('./delivery'));
const StorePage = lazy(() => import('./store'));
const CartPage = lazy(() => import('./cart'));
const SupplyPage = lazy(() => import('./supply'));
const ChatsPages = lazy(() => import('./chats'));
const StatisticsPage = lazy(() => import('./statistics'));
const TransactionsPage = lazy(() => import('./transactions'));
const MainPage = lazy(() => import('./main'));
const BannersPage = lazy(() => import('./banners'));

const HomePage: React.FC<TPropsFromRedux & { userLastLocation: any }> = ({
  userRoles,
  isAuthorized,
  setMenuConfig,
  getOrCreateCart,
  getOrCreateCartLoading,
  getLocalGuestCart,
  isAdmin,
  fetchMe,
}) => {
  const [menuConfig] = useState(getMenuConfig(userRoles, isAuthorized));
  const history = useHistory();

  useEffect(() => {
    fetchMe();
  }, []);

  useEffect(() => {
    setMenuConfig(menuConfig);
  }, [setMenuConfig, menuConfig]);

  // Проверка на существование корзины у пользователя
  useEffect(() => {
    const localCart = localStorage.getItem('guestCart');
    if (userRoles && userRoles.includes(UserRoles.ROLE_BUYER) && localCart) {
      const localLocation = localStorage.getItem('location');
      const parseLocalLocation = localLocation && JSON.parse(localLocation);
      if (parseLocalLocation) {
        localStorage.removeItem('location');
      }
      // history.replace('/cart');
    }

    if (userRoles && !userRoles.includes(UserRoles.ROLE_VENDOR) && !isAdmin) {
      getOrCreateCart();
    }
  }, [getOrCreateCart, isAdmin, userRoles]);

  useEffect(() => {
    !isAuthorized && getLocalGuestCart();
  }, [isAuthorized, getLocalGuestCart]);

  if (getOrCreateCartLoading) return <Preloader />;

  return (
    <Suspense fallback={<LayoutSplashScreen />}>
      <Switch>
        <Redirect exact from='/' to='/main/catalog' />
        <PrivateRoute path='/settings' component={SettingsPage} isAuthorized={isAuthorized} />
        <PrivateRoute path='/categories' component={CategoriesPage} isAuthorized={isAuthorized}/>
        <Route path='/products' component={ProductsPage} />
        <PrivateRoute path='/documents' component={UserDocPage} isAuthorized={isAuthorized}/>
        <PrivateRoute path='/users' component={UsersPage} isAuthorized={isAuthorized}/>
        <PrivateRoute path='/companies' component={CompaniesPage} isAuthorized={isAuthorized}/>
        <PrivateRoute path='/orders' component={OrdersPage} isAuthorized={isAuthorized}/>
        <PrivateRoute path='/delivery' component={DeliveryPage} isAuthorized={isAuthorized}/>
        <PrivateRoute path='/stores' component={StorePage} isAuthorized={isAuthorized}/>
        <Route path='/cart' component={CartPage} />
        <PrivateRoute path='/supply' component={SupplyPage} isAuthorized={isAuthorized}/>
        <Route path='/blog' component={BlogPage} />
        <Route path='/viewproduct/:id' component={ProductView} />
        <Route path='/viewcompany/:id' component={CompanyView} />
        <PrivateRoute path='/banners' component={BannersPage} isAuthorized={isAuthorized}/>
        <PrivateRoute path='/promocodes' component={PromocodesPage} isAuthorized={isAuthorized}/>
        <PrivateRoute path='/refferals' component={RefferalsPage} isAuthorized={isAuthorized}/>
        <PrivateRoute path='/chats' component={ChatsPages} isAuthorized={isAuthorized}/>
        <PrivateRoute path='/statistics' component={StatisticsPage} isAuthorized={isAuthorized}/>
        <PrivateRoute path='/transactions' component={TransactionsPage} isAuthorized={isAuthorized}/>
        <Route path='/main' component={MainPage} />
        <Redirect to='/error/error-v1' />
      </Switch>
    </Suspense>
  );
};

const connector = connect(
  (state: IAppState) => ({
    userRoles: state.auth.user?.roles,
    isAuthorized: state.auth.user != null,
    getOrCreateCartLoading: state.cart.getOrCreateCartLoading,
    isAdmin: state.auth.user?.is_admin,
  }),
  {
    fetchMe: profileActions.fetchRequest,
    editMe: profileActions.editRequest,
    setMenuConfig: builder.actions.setMenuConfig,
    getOrCreateCart: cartActions.getOrCreateRequest,
    getLocalGuestCart: cartActions.getLocalGuestCart,
    getOrCreateSupply: supplyActions.getOrCreateRequest,
  }
);

type TPropsFromRedux = ConnectedProps<typeof connector>;

export default connector(HomePage);
